import { createSlice } from "@reduxjs/toolkit";

import { patchStoredAuthSession, type StoredAuthSession } from "./authSessionStorage";
import { setPermissions, clearPermissions } from "./permissionsSlice";

import {
  restoreSupportSession,
  exitSupportSession,
} from "@/features/platformOwner/platformSessionSlice";

export type UserRole =
  | "super_admin"
  | "admin"
  | "teacher"
  | "student"
  | "parent"
  | "staff";
export type ActiveContextType = "Guardian" | "Teacher" | "Admin";

/** Data scope levels — determines row-level data visibility. */
export type DataScopeType = "Linked" | "Assigned" | "Unrestricted" | "Self";

export interface DomainIdentity {
  tenantId: string;
  domainType: string;
  domainEntityId: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  /** Optional system key (e.g., guardian) for non-staff personas. */
  key?: string;
  schoolId: string;
  avatar?: string;
  firstName?: string;
  lastName?: string;
  gender?: "male" | "female" | "other" | "prefer_not_to_say";
  dateOfBirth?: string; // YYYY-MM-DD
  phone?: string;
  accessToken?: string;
  refreshToken?: string;
  tenantId?: string;
  tenantName?: string;
  /** The broadest data scope across all user roles. */
  dataScope?: DataScopeType;
  /** Domain identities resolved for this user (Employee, Guardian, etc.). */
  domainIdentities?: DomainIdentity[];
  /** Primary domain type for the current tenant (e.g. "Employee" or "Guardian"). */
  domainType?: string;
  /** Active tenant-scoped session context. */
  activeContext?: ActiveContextType | string;
  /** Active domain identity selected during activation, when applicable. */
  activeDomainEntityId?: string;
  /** "PLATFORM" for a platform-role session with no tenant, "TENANT" otherwise. */
  scope?: "PLATFORM" | "TENANT";
  /** Platform role names held by the caller (e.g. ["PlatformOwner"]). */
  platformRoles?: string[];
  /** True while acting inside a tenant via an active Platform Support Session. */
  isPlatformSupportSession?: boolean;
  /** Backing PlatformSupportSession id, set only while isPlatformSupportSession is true. */
  platformSupportSessionId?: string;
}

/** Guid.Empty as returned by the backend for a PLATFORM-scope session's TenantId. */
export const EMPTY_TENANT_ID = "00000000-0000-0000-0000-000000000000";

/** Normalizes the backend's Guid.Empty sentinel to undefined for "no tenant". */
export function normalizeTenantId(
  tenantId: string | undefined | null,
): string | undefined {
  if (!tenantId || tenantId === EMPTY_TENANT_ID) return undefined;
  return tenantId;
}

export interface BootstrapTenant {
  tenantId: string;
  tenantName: string;
}

export interface BootstrapContext {
  tenantId: string;
  tenantName: string;
  contextType: string;
  domainEntityId?: string | null;
  roles: string[];
  dataScope: string;
}

export interface PendingBootstrapSession {
  userId: string;
  userName: string;
  email: string;
  phoneNumber: string;
  bootstrapToken: string;
  expiresAt: number;
  availableTenants: BootstrapTenant[];
  availableContexts: BootstrapContext[];
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;

  // App bootstrapping
  initialized: boolean;

  // OTP UI helpers (used by Login screen)
  otpSentTo: string | null; // phone number
  mockOtp: string | null; // dev/test OTP if backend returns it
  pendingBootstrap: PendingBootstrapSession | null;
}

const AUTH_SESSION_STORAGE_KEY = "auth_session_v1";

function normalizeRole(roles: string[] | undefined | null): UserRole {
  const normalized = (roles ?? []).map((r) => String(r).toLowerCase());
  if (normalized.some((r) => r.includes("super"))) return "super_admin";
  if (normalized.some((r) => r.includes("admin"))) return "admin";
  if (normalized.some((r) => r.includes("teacher"))) return "teacher";
  if (normalized.some((r) => r.includes("student"))) return "student";
  if (normalized.some((r) => r.includes("parent") || r.includes("guardian")))
    return "parent";
  return normalized.length > 0 ? "staff" : "parent";
}

/**
 * Initialize auth + permissions from persisted OTP verify response.
 * When the access token is expired but a refresh token is available,
 * attempts a silent refresh before giving up.
 */
export const initializeAuthAsync = () => {
  return async (dispatch: (action: unknown) => unknown) => {
    const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    const tokenExpiryRaw = localStorage.getItem("tokenExpiry");

    if (!raw) {
      dispatch(authSlice.actions.setInitialized(true));
      return { restored: false };
    }

    try {
      const session = JSON.parse(raw) as StoredAuthSession;
      const tokenExpiry = Number(tokenExpiryRaw);
      const now = Math.floor(Date.now() / 1000);
      const storedPlatformSession = session.platformSession;

      if (!Number.isFinite(tokenExpiry) || tokenExpiry <= now) {
        // A Platform Support Session token is a standalone 60-minute credential with no
        // refresh path (mirrors the same guard in axios.ts). The generic refresh below would
        // otherwise spend the platform owner's still-present refreshToken and hand back a
        // PLATFORM-scope token while the restored UI still believed it was inside the tenant.
        // Expiry here means the session is over, so restore the operator's own session.
        if (storedPlatformSession?.active) {
          const { homeAccessToken, homeTokenExpiry, homeUser, homePermissionCodes } =
            storedPlatformSession;
          const homeExpiry = Number(homeTokenExpiry);

          if (
            homeAccessToken &&
            homeUser &&
            Number.isFinite(homeExpiry) &&
            homeExpiry > now
          ) {
            localStorage.setItem("accessToken", homeAccessToken);
            localStorage.setItem("tokenExpiry", String(homeExpiry));
            patchStoredAuthSession({ user: homeUser, platformSession: undefined });

            dispatch(exitSupportSession());
            dispatch(setPermissions(homePermissionCodes ?? []));
            dispatch(authSlice.actions.setUser(homeUser));
            dispatch(authSlice.actions.setInitialized(true));
            return { restored: true, user: homeUser };
          }

          // The platform owner's own token expired too — nothing left to fall back to.
          dispatch(authSlice.actions.logout());
          dispatch(authSlice.actions.setInitialized(true));
          return { restored: false };
        }

        // Access token expired — try silent refresh
        const refreshTokenValue = localStorage.getItem("refreshToken");
        const deviceId = localStorage.getItem("device_identifier");
        const userId = localStorage.getItem("userId");

        if (refreshTokenValue && deviceId && userId) {
          try {
            const { refreshToken: refreshTokenFn } =
              await import("@/services/authService");
            const result = await refreshTokenFn(refreshTokenValue, deviceId, userId);

            // Persist new tokens
            localStorage.setItem("accessToken", result.accessToken);
            localStorage.setItem("refreshToken", result.refreshToken);
            const newExpiry = Math.floor(Date.now() / 1000) + result.expiresIn;
            localStorage.setItem("tokenExpiry", String(newExpiry));

            // Update session user with new tokens
            const refreshedTenantId = normalizeTenantId(result.tenantId);
            const updatedUser: User = {
              ...session.user,
              role: normalizeRole(result.roles),
              key: result.activeContext?.toLowerCase?.() ?? session.user.key,
              schoolId: result.tenantId,
              tenantId: refreshedTenantId,
              tenantName: result.tenantName ?? session.user.tenantName,
              accessToken: result.accessToken,
              refreshToken: result.refreshToken,
              dataScope: (result.dataScope ??
                session.user.dataScope ??
                "Unrestricted") as DataScopeType,
              domainIdentities: result.domainIdentities ?? session.user.domainIdentities,
              domainType:
                result.domainIdentities?.find(
                  (identity) =>
                    identity.tenantId === result.tenantId &&
                    identity.domainEntityId === session.user.activeDomainEntityId,
                )?.domainType ??
                result.domainIdentities?.find(
                  (identity) => identity.tenantId === result.tenantId,
                )?.domainType ??
                session.user.domainType,
              activeContext: result.activeContext ?? session.user.activeContext,
              activeDomainEntityId: session.user.activeDomainEntityId,
              scope: result.scope ?? session.user.scope,
              platformRoles: result.platformRoles ?? session.user.platformRoles,
            };

            const updatedSession: StoredAuthSession = {
              ...session,
              user: updatedUser,
              tokenExpiry: String(newExpiry),
            };
            localStorage.setItem(
              AUTH_SESSION_STORAGE_KEY,
              JSON.stringify(updatedSession),
            );

            dispatch(setPermissions(session.effectivePermissionKeys ?? []));
            dispatch(authSlice.actions.setUser(updatedUser));
            dispatch(authSlice.actions.setInitialized(true));
            return { restored: true, user: updatedUser };
          } catch {
            // Refresh failed — force logout
            dispatch(authSlice.actions.logout());
            dispatch(authSlice.actions.setInitialized(true));
            return { restored: false };
          }
        }

        // No refresh token, force unauthenticated
        dispatch(authSlice.actions.logout());
        return { restored: false };
      }

      // Token still valid — restore from storage.
      // The support session is restored alongside the identity it belongs to, from the same
      // envelope, so the banner/Exit and the swapped user can never come back out of step:
      // before this, `user.isPlatformSupportSession` survived a reload but the slice backing
      // the banner did not, leaving an operator inside a tenant with no visible way out.
      if (storedPlatformSession?.active) {
        dispatch(restoreSupportSession(storedPlatformSession));
      }
      dispatch(setPermissions(session.effectivePermissionKeys ?? []));
      dispatch(authSlice.actions.setUser(session.user));
      dispatch(authSlice.actions.setInitialized(true));
      return { restored: true, user: session.user };
    } catch {
      dispatch(authSlice.actions.logout());
      dispatch(authSlice.actions.setInitialized(true));
      return { restored: false };
    }
  };
};

/* ------------------------------------------------- */

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,

  initialized: false,

  otpSentTo: null,
  mockOtp: null,
  pendingBootstrap: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      if (action.payload) {
        state.pendingBootstrap = null;
      }
    },
    setInitialized: (state, action) => {
      state.initialized = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    patchUser: (state, action) => {
      if (!state.user) return;
      state.user = {
        ...state.user,
        ...(action.payload ?? {}),
      };
    },
    setPendingBootstrap: (state, action) => {
      state.pendingBootstrap = action.payload;
      state.user = null;
      state.isAuthenticated = false;
    },
    clearPendingBootstrap: (state) => {
      state.pendingBootstrap = null;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.otpSentTo = null;
      state.mockOtp = null;
      state.pendingBootstrap = null;
      // Clear tokens
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenExpiry");
      localStorage.removeItem("userId");
      localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
      // Clear permissions
      clearPermissions();
    },
    clearOtp: (state) => {
      state.otpSentTo = null;
      state.mockOtp = null;
      state.error = null;
      state.pendingBootstrap = null;
    },
    setOtpSentTo: (state, action) => {
      state.otpSentTo = action.payload;
    },
    setMockOtp: (state, action) => {
      state.mockOtp = action.payload;
    },
  },
});

export const {
  setUser,
  setInitialized,
  setLoading,
  setError,
  patchUser,
  setPendingBootstrap,
  clearPendingBootstrap,
  logout,
  clearOtp,
  setOtpSentTo,
  setMockOtp,
} = authSlice.actions;
export { normalizeRole };

export default authSlice.reducer;
